import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Wallet, LineChart, ArrowUp, ArrowDown, RefreshCcw, DollarSign, TrendingUp, BarChart2, Users, ChevronDown, ChevronUp, HelpCircle, Copy, Check, Share2, Download, X, Upload, Image, Loader, Eye, EyeOff } from 'lucide-react';
import GlobalDropdown from '../../components/GlobalDropdown';
import { useWalletUser } from '../../hooks/useWalletUser';
import { getUserTradingWallets, TradingWallet } from '../../services/tradingWalletService';
import { getUserProfile } from '../../services/userProfileService';
import { getWalletProfitAndLoss, getWalletTokensWithMetadata, getTokenInfo } from '../../services/solanatrackerService';
import { wsService } from '../../services/websocketService';

interface ProfitAndLossProps {
  onClose?: () => void;
  isWidget?: boolean;
  walletAddress?: string;
}

// Add interface for token metadata
interface TokenMetadata {
  mint: string;
  symbol?: string;
  name?: string;
  image?: string | null;
  decimals?: number;
}

interface TokenData {
  holding: number;
  total: number;
  total_invested: number;
  realized: number;
  unrealized: number;
  current_value: number;
  cost_basis: number;
}

// Wallet option interface for wallet selector
interface WalletOption {
  address: string;
  name: string;
  type: 'main' | 'trading';
  isActive?: boolean;
  balance?: number;
}

// 🔥 **SHARE SYSTEM INTERFACES**: Add complete share system interfaces
interface ShareData {
  type: 'token' | 'wallet';
  tokenMint?: string;
  tokenSymbol?: string;
  tokenName?: string;
  tokenImage?: string | null;
  pnl: number;
  roi?: number;
  invested?: number;
  currentValue?: number;
  walletAddress?: string;
  walletName?: string;
  totalTokens?: number;
  winRate?: number;
}

interface ShareCustomization {
  customText: string;
  showPrimaryWallet: boolean;
  showTradingWallet: boolean;
  showExactAmounts: boolean;
  showROI: boolean;
  showInvested: boolean;
  showCurrentValue: boolean;
  showTokenCount: boolean;
  showWinRate: boolean;
  showTokenImage: boolean;
  useTokenAsBackground: boolean;
  tokenBackgroundOpacity: number;
  currencyDisplay: 'SOL' | 'USD';
  backgroundStyle: 'gradient' | 'solid' | 'pattern' | 'custom' | 'token';
  colorScheme: 'green' | 'blue' | 'purple' | 'gold';
  customBackgroundImage: string | null;
}

// Add X Logo component for Twitter sharing
const XLogo: React.FC<{ size?: number; className?: string }> = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const ProfitAndLoss: React.FC<ProfitAndLossProps> = ({ 
  onClose, 
  isWidget = false,
  walletAddress: propWalletAddress
}) => {
  const walletUser = useWalletUser();
  
  // Wallet management state (same as Active Positions)
  const [tradingWallets, setTradingWallets] = useState<TradingWallet[]>([]);
  const [currentViewingWallet, setCurrentViewingWallet] = useState<string | null>(null);
  const [availableWallets, setAvailableWallets] = useState<WalletOption[]>([]);
  const [isSynchronizing, setIsSynchronizing] = useState(false);
  const [walletBalances, setWalletBalances] = useState<{[key: string]: number}>({});
  
  // Existing P&L state
  const [activeTradingWallet, setActiveTradingWallet] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pnlData, setPnlData] = useState<any>(null);
  const [tokenEntries, setTokenEntries] = useState<[string, any][]>([]);
  const [tokenMetadata, setTokenMetadata] = useState<Record<string, TokenMetadata>>({});
  const [copiedTokenMint, setCopiedTokenMint] = useState<string | null>(null);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState<boolean>(false);
  const [sortOption, setSortOption] = useState<string>('total');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showZeroBalance, setShowZeroBalance] = useState<boolean>(false);
  const [showActivePositions, setShowActivePositions] = useState<boolean>(true);
  
  // Hide/Show tokens state
  const [hiddenTokens, setHiddenTokens] = useState<Set<string>>(new Set());
  const [showingHidden, setShowingHidden] = useState<boolean>(false);
  
  // 🔥 **REAL-TIME P&L SYSTEM**: WebSocket subscriptions and real-time prices
  const [activeSubscriptions, setActiveSubscriptions] = useState<Map<string, () => void>>(new Map());
  const [realtimePrices, setRealtimePrices] = useState<{[tokenMint: string]: {priceUsd: number, lastUpdated: number}}>({});
  
  // 🔥 **SHARE SYSTEM STATE**: Complete share system state management
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState<ShareData | null>(null);
  const [shareCustomization, setShareCustomization] = useState<ShareCustomization>({
    customText: '',
    showPrimaryWallet: true,
    showTradingWallet: true,
    showExactAmounts: false,
    showROI: true,
    showInvested: true,
    showCurrentValue: true,
    showTokenCount: true,
    showWinRate: true,
    showTokenImage: true,
    useTokenAsBackground: false,
    tokenBackgroundOpacity: 20,
    currencyDisplay: 'USD',
    backgroundStyle: 'gradient',
    colorScheme: 'green',
    customBackgroundImage: null
  });
  const [generatedCardUrl, setGeneratedCardUrl] = useState<string | null>(null);
  const [isGeneratingCard, setIsGeneratingCard] = useState(false);
  
  // Ref to track loading state and prevent duplicate calls
  const isLoadingRef = useRef(false);
  
  // Cache for metadata to avoid refetching when switching wallets
  const [metadataCache] = useState<Map<string, {metadata: TokenMetadata, timestamp: number}>>(new Map());

  // Use the provided wallet address or the active trading wallet
  const effectiveWalletAddress = propWalletAddress || (walletUser?.walletAddress || null);

  // Add a ref to store the interval ID
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const backgroundPollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Add a state to track when auto-refresh is happening
  const [isAutoRefreshing, setIsAutoRefreshing] = useState<boolean>(false);

  // 🔥 **REAL-TIME P&L CALCULATION**: Calculate real-time P&L for individual tokens
  const calculateRealTimePnL = useCallback((tokenMint: string, tokenPnL: any) => {
    const realtimePrice = realtimePrices[tokenMint];
    if (!realtimePrice) {
      // No real-time price, use original values
      return {
        currentValue: tokenPnL.current_value,
        unrealized: tokenPnL.unrealized,
        total: tokenPnL.total
      };
    }
    
    // Calculate new values based on real-time price
    const holding = tokenPnL.holding;
    const newCurrentValue = holding * realtimePrice.priceUsd;
    
    // Calculate unrealized P&L correctly:
    // For current holdings, we need to compare current value vs cost basis of those holdings
    // cost_basis represents the average price paid per token
    // So the cost of current holdings = holding * cost_basis
    const costOfCurrentHoldings = holding * tokenPnL.cost_basis;
    const newUnrealized = newCurrentValue - costOfCurrentHoldings;
    
    // Total P&L = realized (from past sales) + unrealized (from current holdings)
    const newTotal = tokenPnL.realized + newUnrealized;
    
    return {
      currentValue: newCurrentValue,
      unrealized: newUnrealized,
      total: newTotal,
      isRealTime: true
    };
  }, [realtimePrices]);

  // 🔥 **SHARE SYSTEM FUNCTIONS**: Complete share system implementation
  const handleShareToken = (tokenMint: string, tokenPnL: TokenData) => {
    const metadata = tokenMetadata[tokenMint];
    const realTimePnL = calculateRealTimePnL(tokenMint, tokenPnL);
    const roi = tokenPnL.total_invested > 0 ? (realTimePnL.total / tokenPnL.total_invested) * 100 : 0;
    
    const shareData: ShareData = {
      type: 'token',
      tokenMint,
      tokenSymbol: metadata?.symbol || tokenMint.slice(0, 8),
      tokenName: metadata?.name || 'Unknown Token',
      tokenImage: metadata?.image || null,
      pnl: realTimePnL.total,
      roi,
      invested: tokenPnL.total_invested,
      currentValue: realTimePnL.currentValue
    };
    
    setShareData(shareData);
    setShareCustomization(prev => ({
      ...prev,
      customText: `Just ${realTimePnL.total >= 0 ? 'made' : 'lost'} ${formatCurrency(realTimePnL.total)} on $${shareData.tokenSymbol}! 🚀`
    }));
    setShowShareModal(true);
  };

  const handleShareWallet = () => {
    if (!summaryStats || !currentViewingWallet) return;
    
    const activeTokensCount = sortedTokenEntries.filter(([_, token]) => (token as TokenData).holding > 0).length;
    
    const shareData: ShareData = {
      type: 'wallet',
      pnl: summaryStats.total,
      invested: summaryStats.totalInvested,
      walletAddress: currentViewingWallet,
      totalTokens: activeTokensCount,
      winRate: summaryStats.winPercentage
    };
    
    // Clear previous card URL when switching to wallet share
    setGeneratedCardUrl(null);
    setShareData(shareData);
    setShareCustomization(prev => ({
      ...prev,
      customText: `My P&L: ${summaryStats.total >= 0 ? '+' : ''}${formatCurrency(summaryStats.total)} 📊`
    }));
    setShowShareModal(true);
  };

  const generateShareCard = async () => {
    if (!shareData) return;
    
    setIsGeneratingCard(true);
    
    try {
      // Create canvas for card generation
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');
      
      // Set canvas size (landscape format)
      canvas.width = 1200;
      canvas.height = 630;
      
      // Load Roboto font first
      const robotoFont = new FontFace('Roboto', 'url(https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxK.woff2)');
      await robotoFont.load();
      document.fonts.add(robotoFont);
      
      // Helper function to format P&L values (same as Active Positions)
      const formatPnL = (value: number) => {
        const formatted = formatCurrency(value);
        return value >= 0 ? `+${formatted}` : formatted;
      };
      
      // Background
      if (shareCustomization.backgroundStyle === 'custom' && shareCustomization.customBackgroundImage) {
        // Custom background image at specified opacity
        const img = document.createElement('img');
        img.onload = () => {
          if (ctx) {
            ctx.globalAlpha = shareCustomization.tokenBackgroundOpacity / 100;
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            ctx.globalAlpha = 1.0;
            drawCardContent();
          }
        };
        img.src = shareCustomization.customBackgroundImage;
      } else if (shareCustomization.backgroundStyle === 'token' && shareCustomization.useTokenAsBackground && shareData?.tokenImage) {
        // Token background image at specified opacity
        const tokenBgImg = document.createElement('img');
        tokenBgImg.crossOrigin = 'anonymous';
        tokenBgImg.onload = () => {
          if (ctx) {
            ctx.globalAlpha = shareCustomization.tokenBackgroundOpacity / 100;
            ctx.drawImage(tokenBgImg, 0, 0, canvas.width, canvas.height);
            ctx.globalAlpha = 1.0;
            drawCardContent();
          }
        };
        tokenBgImg.onerror = () => {
          // Fallback to gradient if token image fails
          drawGradientBackground();
          drawCardContent();
        };
        tokenBgImg.src = shareData.tokenImage;
      } else {
        // Default gradient background
        drawGradientBackground();
        drawCardContent();
      }
      
      function drawGradientBackground() {
        if (!ctx) return;
        const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        switch (shareCustomization.colorScheme) {
          case 'green':
            gradient.addColorStop(0, '#16161a');
            gradient.addColorStop(1, '#1a2e1a');
            break;
          case 'blue':
            gradient.addColorStop(0, '#16161a');
            gradient.addColorStop(1, '#1a1e2e');
            break;
          case 'purple':
            gradient.addColorStop(0, '#16161a');
            gradient.addColorStop(1, '#2e1a2e');
            break;
          case 'gold':
            gradient.addColorStop(0, '#16161a');
            gradient.addColorStop(1, '#2e2a1a');
            break;
        }
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      
      function drawCardContent() {
        // Main content with Roboto font
        if (!ctx || !shareData) return;
        
        // Load and draw EKKO logo in top right (bigger and moved in)
        const ekkoLogo = document.createElement('img');
        ekkoLogo.crossOrigin = 'anonymous';
        ekkoLogo.onload = () => {
          // Draw EKKO logo in top right (120x48 size, moved in more)
          if (!ctx) return;
          ctx.drawImage(ekkoLogo, canvas.width - 140, 15, 120, 48);
          drawMainContent();
        };
        ekkoLogo.onerror = () => {
          // Fallback if logo fails to load
          if (!ctx) return;
          ctx.fillStyle = '#24c35b';
          ctx.font = 'bold 28px Roboto, Arial, sans-serif';
          ctx.textAlign = 'right';
          ctx.fillText('EKKO', canvas.width - 25, 45);
          ctx.textAlign = 'left';
          drawMainContent();
        };
        ekkoLogo.src = 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3.png';
        
        function drawMainContent() {
          // Load and draw Solana logo in bottom right (bigger and moved in)
          const solanaLogo = document.createElement('img');
          solanaLogo.crossOrigin = 'anonymous';
          solanaLogo.onload = async () => {
            // Draw Solana logo in bottom right (50x50 size, moved in more)
            if (!ctx) return;
            ctx.drawImage(solanaLogo, canvas.width - 70, canvas.height - 70, 50, 50);
            await drawContent();
          };
          solanaLogo.onerror = async () => {
            // Fallback Solana logo (bigger)
            if (!ctx) return;
            ctx.beginPath();
            ctx.arc(canvas.width - 45, canvas.height - 45, 25, 0, 2 * Math.PI);
            ctx.fillStyle = '#9945FF';
            ctx.fill();
            ctx.fillStyle = 'white';
            ctx.font = 'bold 14px Roboto, Arial, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('SOL', canvas.width - 45, canvas.height - 40);
            ctx.textAlign = 'left';
            await drawContent();
          };
          solanaLogo.src = 'https://solana.com/src/img/branding/solanaLogoMark.svg';
        }
        
        async function drawContent() {
          // Main content with Roboto font and improved layout
          if (!ctx || !shareData) return;
          
          ctx.textAlign = 'left';
          ctx.fillStyle = 'white';
          
          if (shareData.type === 'token') {
            // Token share card with image support
            let contentStartX = 50;
            let tokenImageSize = 100; // Larger size for better visibility
            let currentY = 120; // Start below EKKO logo
            
            // Load and draw token image if available and enabled first
            if (shareData.tokenImage && shareCustomization.showTokenImage) {
              try {
                const tokenImg = document.createElement('img');
                tokenImg.crossOrigin = 'anonymous';
                
                let hasDrawn = false; // Prevent duplicate drawing
                
                const imageLoadPromise = new Promise<void>((resolve) => {
                  tokenImg.onload = () => {
                    if (!hasDrawn) {
                      hasDrawn = true;
                      drawTokenContent(true, tokenImg);
                    }
                    resolve();
                  };
                  tokenImg.onerror = () => {
                    if (!hasDrawn) {
                      hasDrawn = true;
                      drawTokenContent(false, null);
                    }
                    resolve();
                  };
                  tokenImg.src = shareData.tokenImage;
                });
                
                // Add timeout for image loading
                const timeoutPromise = new Promise<void>((resolve) => {
                  setTimeout(() => {
                    if (!hasDrawn) {
                      hasDrawn = true;
                      drawTokenContent(false, null);
                    }
                    resolve();
                  }, 2000);
                });
                
                // Wait for either image load or timeout
                await Promise.race([imageLoadPromise, timeoutPromise]);
              } catch (error) {
                console.warn('Token image loading failed:', error);
                drawTokenContent(false, null);
              }
            } else {
              drawTokenContent(false, null);
            }
            
            function drawTokenContent(hasImage: boolean, tokenImg: HTMLImageElement | null) {
              if (!ctx || !shareData) return;
              
              // Token symbol at the top left
              ctx.fillStyle = 'white';
              ctx.font = 'bold 48px Roboto, Arial, sans-serif';
              ctx.fillText(`$${shareData.tokenSymbol}`, contentStartX, currentY);
              
              // Draw token image next to the symbol if available
              if (hasImage && tokenImg) {
                try {
                  // Measure text width to position image next to it
                  const symbolWidth = ctx.measureText(`$${shareData.tokenSymbol}`).width;
                  
                  // Draw circular token image next to the symbol with aspect ratio preservation
                  const imageX = contentStartX + symbolWidth + 30; // 30px gap after symbol for larger image
                  const imageY = currentY - tokenImageSize + 5; // Better alignment with larger image
                  
                  ctx.save();
                  ctx.beginPath();
                  ctx.arc(imageX + tokenImageSize/2, imageY + tokenImageSize/2, tokenImageSize/2, 0, 2 * Math.PI);
                  ctx.clip();
                  
                  // Calculate aspect ratio and draw image to fill circle while maintaining ratio (like object-fit: cover)
                  const imgAspectRatio = tokenImg.naturalWidth / tokenImg.naturalHeight;
                  const circleAspectRatio = 1; // Circle is always 1:1
                  
                  let drawWidth, drawHeight, drawX, drawY;
                  
                  if (imgAspectRatio > circleAspectRatio) {
                    // Image is wider - fit by height and crop sides
                    drawHeight = tokenImageSize;
                    drawWidth = tokenImageSize * imgAspectRatio;
                    drawX = imageX - (drawWidth - tokenImageSize) / 2;
                    drawY = imageY;
                  } else {
                    // Image is taller or square - fit by width and crop top/bottom
                    drawWidth = tokenImageSize;
                    drawHeight = tokenImageSize / imgAspectRatio;
                    drawX = imageX;
                    drawY = imageY - (drawHeight - tokenImageSize) / 2;
                  }
                  
                  ctx.drawImage(tokenImg, drawX, drawY, drawWidth, drawHeight);
                  ctx.restore();
                  
                  // Draw border around token image
                  ctx.beginPath();
                  ctx.arc(imageX + tokenImageSize/2, imageY + tokenImageSize/2, tokenImageSize/2, 0, 2 * Math.PI);
                  ctx.strokeStyle = '#24c35b';
                  ctx.lineWidth = 3;
                  ctx.stroke();
                } catch (error) {
                  console.warn('Error drawing token image:', error);
                }
              }
              
              currentY += 50;
              
              // Token name below symbol
              ctx.font = 'bold 24px Roboto, Arial, sans-serif';
              ctx.fillStyle = '#888';
              ctx.fillText(shareData.tokenName || '', contentStartX, currentY);
              currentY += 80; // More space before P&L
              
              // PnL (main stat) - centered vertically
              ctx.font = 'bold 64px Roboto, Arial, sans-serif';
              ctx.fillStyle = shareData.pnl >= 0 ? '#24c35b' : '#ef4444';
              const pnlText = shareCustomization.currencyDisplay === 'SOL' 
                ? `${shareData.pnl >= 0 ? '+' : ''}${(shareData.pnl / 200).toFixed(4)} SOL`
                : formatPnL(shareData.pnl);
              ctx.fillText(pnlText, contentStartX, currentY);
              currentY += 80;
              
              if (shareCustomization.showROI && shareData.roi !== undefined) {
                ctx.font = 'bold 36px Roboto, Arial, sans-serif';
                ctx.fillStyle = shareData.roi >= 0 ? '#24c35b' : '#ef4444';
                ctx.fillText(`${shareData.roi >= 0 ? '+' : ''}${shareData.roi.toFixed(2)}% ROI`, contentStartX, currentY);
                currentY += 50;
              }
              
              if (shareCustomization.showInvested && shareData.invested) {
                ctx.font = 'bold 28px Roboto, Arial, sans-serif';
                ctx.fillStyle = '#aaa';
                const investedText = shareCustomization.currencyDisplay === 'SOL'
                  ? `Invested: ${(shareData.invested / 200).toFixed(4)} SOL`
                  : `Invested: ${formatCurrency(shareData.invested)}`;
                ctx.fillText(investedText, contentStartX, currentY);
                currentY += 40;
              }
              
              if (shareCustomization.showCurrentValue && shareData.currentValue) {
                ctx.font = 'bold 28px Roboto, Arial, sans-serif';
                ctx.fillStyle = '#aaa';
                const currentValueText = shareCustomization.currencyDisplay === 'SOL'
                  ? `Current: ${(shareData.currentValue / 200).toFixed(4)} SOL`
                  : `Current: ${formatCurrency(shareData.currentValue)}`;
                ctx.fillText(currentValueText, contentStartX, currentY);
                currentY += 40;
              }
              
              // Continue with custom text and timestamp
              drawCommonContent();
            }
            
          } else {
            // Wallet share card
            drawWalletContent();
          }
          
          function drawWalletContent() {
            if (!ctx || !shareData) return;
            
            let currentY = 120; // Start below EKKO branding
            
            ctx.font = 'bold 48px Roboto, Arial, sans-serif';
            ctx.fillStyle = 'white';
            ctx.fillText('Portfolio Performance', 50, currentY);
            currentY += 80;
            
            // Total PnL
            ctx.font = 'bold 64px Roboto, Arial, sans-serif';
            ctx.fillStyle = shareData.pnl >= 0 ? '#24c35b' : '#ef4444';
            const walletPnlText = shareCustomization.currencyDisplay === 'SOL'
              ? `${shareData.pnl >= 0 ? '+' : ''}${(shareData.pnl / 200).toFixed(4)} SOL`
              : formatPnL(shareData.pnl);
            ctx.fillText(walletPnlText, 50, currentY);
            currentY += 80;
            
            if (shareCustomization.showInvested && shareData.invested) {
              ctx.font = 'bold 24px Roboto, Arial, sans-serif';
              ctx.fillStyle = '#888';
              const totalInvestedText = shareCustomization.currencyDisplay === 'SOL'
                ? `Total Invested: ${(shareData.invested / 200).toFixed(4)} SOL`
                : `Total Invested: ${formatCurrency(shareData.invested)}`;
              ctx.fillText(totalInvestedText, 50, currentY);
              currentY += 40;
            }
            
            if (shareCustomization.showWinRate && shareData.winRate !== undefined) {
              ctx.font = 'bold 24px Roboto, Arial, sans-serif';
              ctx.fillStyle = '#888';
              ctx.fillText(`Win Rate: ${shareData.winRate.toFixed(0)}%`, 50, currentY);
              currentY += 40;
            }
            
            if (shareCustomization.showTokenCount && shareData.totalTokens) {
              ctx.font = 'bold 24px Roboto, Arial, sans-serif';
              ctx.fillStyle = '#888';
              ctx.fillText(`${shareData.totalTokens} Active Positions`, 50, currentY);
              currentY += 40;
            }
            
            // Wallet addresses
            if (shareCustomization.showPrimaryWallet && walletUser?.walletAddress) {
              ctx.font = 'bold 20px Roboto, monospace';
              ctx.fillStyle = '#888';
              ctx.fillText(`Primary: ${walletUser.walletAddress.slice(0, 8)}...${walletUser.walletAddress.slice(-8)}`, 50, currentY);
              currentY += 35;
            }
            
            if (shareCustomization.showTradingWallet && shareData.walletAddress) {
              ctx.font = 'bold 20px Roboto, monospace';
              ctx.fillStyle = '#888';
              ctx.fillText(`Trading: ${shareData.walletAddress.slice(0, 8)}...${shareData.walletAddress.slice(-8)}`, 50, currentY);
              currentY += 35;
            }
            
            drawCommonContent();
          }
          
          function drawCommonContent() {
            if (!ctx || !shareData) return;
            
            // Custom text - positioned to avoid overlap
            if (shareCustomization.customText) {
              ctx.font = 'bold 32px Roboto, Arial, sans-serif';
              ctx.fillStyle = 'white';
              ctx.fillText(shareCustomization.customText, 50, canvas.height - 100);
            }
            
            // Timestamp - bottom left
            ctx.font = 'bold 22px Roboto, Arial, sans-serif';
            ctx.fillStyle = '#666';
            ctx.fillText(new Date().toLocaleDateString(), 50, canvas.height - 40);
            
            // Solana logo - bottom right
            try {
              const solanaImg = new Image();
              solanaImg.crossOrigin = 'anonymous';
              
              const solanaPromise = new Promise<void>((resolve) => {
                solanaImg.onload = () => {
                  try {
                    // Draw Solana logo in bottom right
                    const logoSize = 40;
                    const logoX = canvas.width - logoSize - 20;
                    const logoY = canvas.height - logoSize - 20;
                    ctx.drawImage(solanaImg, logoX, logoY, logoSize, logoSize);
                  } catch (error) {
                    console.warn('Error drawing Solana logo:', error);
                  }
                  resolve();
                };
                solanaImg.onerror = () => {
                  // Fallback Solana logo
                  try {
                    const logoSize = 40;
                    const logoX = canvas.width - logoSize - 20;
                    const logoY = canvas.height - logoSize - 20;
                    
                    ctx.beginPath();
                    ctx.arc(logoX + logoSize/2, logoY + logoSize/2, logoSize/2, 0, 2 * Math.PI);
                    ctx.fillStyle = '#9945FF';
                    ctx.fill();
                    ctx.fillStyle = 'white';
                    ctx.font = 'bold 14px Roboto, Arial, sans-serif';
                    ctx.textAlign = 'center';
                    ctx.fillText('SOL', logoX + logoSize/2, logoY + logoSize/2 + 4);
                    ctx.textAlign = 'left';
                  } catch (error) {
                    console.warn('Error drawing fallback Solana logo:', error);
                  }
                  resolve();
                };
                solanaImg.src = 'https://cryptologos.cc/logos/solana-sol-logo.png';
              });
              
              // Add timeout for logo loading
              const logoTimeout = new Promise<void>((resolve) => {
                setTimeout(() => resolve(), 1000);
              });
              
              Promise.race([solanaPromise, logoTimeout]).then(() => {
                // Convert to blob and create URL after everything is drawn
                canvas.toBlob((blob) => {
                  if (blob) {
                    const url = URL.createObjectURL(blob);
                    setGeneratedCardUrl(url);
                  }
                }, 'image/png');
              });
            } catch (error) {
              console.warn('Solana logo loading failed:', error);
              // Still generate the card without the logo
              canvas.toBlob((blob) => {
                if (blob) {
                  const url = URL.createObjectURL(blob);
                  setGeneratedCardUrl(url);
                }
              }, 'image/png');
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Error generating share card:', error);
    } finally {
      setIsGeneratingCard(false);
    }
  };

  const downloadCard = () => {
    if (!generatedCardUrl || !shareData) return;
    
    const link = document.createElement('a');
    link.href = generatedCardUrl;
    link.download = `ekko-${shareData.type}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const shareToTwitter = () => {
    if (!shareData) return;
    
    const text = shareCustomization.customText || 
      (shareData.type === 'token' 
        ? `Just ${shareData.pnl >= 0 ? 'made' : 'lost'} ${formatPnL(shareData.pnl)} on $${shareData.tokenSymbol}!`
        : `Portfolio Update: ${shareData.pnl >= 0 ? 'Up' : 'Down'} ${formatPnL(shareData.pnl)}!`);
    
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text + ' 🚀\n\nPowered by $EKKO @EKKOPlatform')}&url=${encodeURIComponent('https://ekko-platform.com')}`;
    window.open(url, '_blank');
  };

  // Auto-generate card when share data or customization changes
  useEffect(() => {
    if (shareData && showShareModal && !isGeneratingCard) {
      // Small delay to ensure modal is fully rendered
      const timer = setTimeout(() => {
        generateShareCard();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [shareData, shareCustomization, showShareModal]);

  // Clear generated card URL when modal is closed
  useEffect(() => {
    if (!showShareModal) {
      setGeneratedCardUrl(null);
    }
  }, [showShareModal]);

  // Calculate summary statistics based on active positions
  const summaryStats = useMemo(() => {
    if (!pnlData) return null;

    if (showActivePositions) {
      // Filter tokens with balance > 0
      const activeTokens = Object.entries(pnlData.tokens).filter(([_, token]) => (token as TokenData).holding > 0);
      
      // Calculate totals from active tokens only with real-time prices
      let total = 0;
      let totalInvested = 0;
      let realized = 0;
      let unrealized = 0;
      let currentValue = 0;
      let hasRealtimeData = false;
      
      activeTokens.forEach(([tokenMint, tokenData]) => {
        const token = tokenData as TokenData;
        
        // Use real-time calculation if available
        const realTimePnL = calculateRealTimePnL(tokenMint, token);
        if (realTimePnL.isRealTime) {
          hasRealtimeData = true;
        }
        
        total += realTimePnL.total;
        totalInvested += token.total_invested;
        realized += token.realized;
        unrealized += realTimePnL.unrealized;
        currentValue += realTimePnL.currentValue;
      });
      
      // Calculate win/loss stats from active tokens
      const totalWins = activeTokens.filter(([tokenMint, token]) => {
        const realTimePnL = calculateRealTimePnL(tokenMint, token as TokenData);
        return realTimePnL.total > 0;
      }).length;
      const totalLosses = activeTokens.filter(([tokenMint, token]) => {
        const realTimePnL = calculateRealTimePnL(tokenMint, token as TokenData);
        return realTimePnL.total < 0;
      }).length;
      const totalTrades = totalWins + totalLosses;
      const winPercentage = totalTrades > 0 ? (totalWins / totalTrades) * 100 : 0;
      const lossPercentage = totalTrades > 0 ? (totalLosses / totalTrades) * 100 : 0;

      return {
        total,
        totalInvested,
        realized,
        unrealized,
        currentValue,
        totalWins,
        totalLosses,
        winPercentage,
        lossPercentage,
        hasRealtimeData
      };
    }

    // Return original summary if not showing active positions
    return pnlData.summary;
  }, [pnlData, showActivePositions, realtimePrices, calculateRealTimePnL]);

  // 🔥 **WEBSOCKET SUBSCRIPTIONS**: Subscribe to token price feeds
  const subscribeToTokenPrices = useCallback(async (tokenMints: string[]) => {
    if (!tokenMints.length) return;
    
    console.log(`🔔 P&L Widget: Subscribing to ${tokenMints.length} token price feeds:`, 
      tokenMints.map(mint => mint.slice(0, 8) + '...')
    );
    
    // Clear previous subscriptions to avoid duplicates
    unsubscribeFromAllTokens();
    
    const newSubscriptions = new Map<string, () => void>();
    
    tokenMints.forEach(tokenMint => {
      try {
        // Subscribe to token room for real-time updates
        const roomId = `token:${tokenMint}`;
        console.log(`🔔 P&L Widget: Attempting to subscribe to room: ${roomId}`);
        wsService.joinRoom(roomId);
        
        // Set up event listener for this token
        const unsubscribe = wsService.on(roomId, (data: any) => {
          if (data && data.price && data.price.usd) {
            console.log(`💰 P&L Widget: Price update for ${tokenMint.slice(0, 8)}...: $${data.price.usd}`);
            
            setRealtimePrices(prev => ({
              ...prev,
              [tokenMint]: {
                priceUsd: data.price.usd,
                lastUpdated: data.lastUpdated || Date.now()
              }
            }));
          } else {
            console.warn(`⚠️ P&L Widget: Invalid price data received for ${tokenMint.slice(0, 8)}...`, data);
          }
        });
        
        newSubscriptions.set(tokenMint, () => {
          unsubscribe();
          wsService.leaveRoom(roomId);
        });
        
        console.log(`✅ P&L Widget: Successfully subscribed to ${tokenMint.slice(0, 8)}...`);
        
      } catch (error) {
        console.error(`❌ P&L Widget: Failed to subscribe to token ${tokenMint.slice(0, 8)}...:`, error);
      }
    });
    
    // Update active subscriptions
    setActiveSubscriptions(prev => {
      const updated = new Map(prev);
      newSubscriptions.forEach((unsubscribe, tokenMint) => {
        updated.set(tokenMint, unsubscribe);
      });
      return updated;
    });
    
    console.log(`✅ P&L Widget: Total subscriptions: ${newSubscriptions.size}/${tokenMints.length} successful`);
    
    // Add debugging info for tokens without real-time data after a delay
    setTimeout(() => {
      const tokensWithoutRealtime = tokenMints.filter(mint => !realtimePrices[mint]);
      if (tokensWithoutRealtime.length > 0) {
        console.warn(`⚠️ P&L Widget: Tokens not receiving real-time data (${tokensWithoutRealtime.length}):`, 
          tokensWithoutRealtime.map(mint => mint.slice(0, 8) + '...')
        );
      }
    }, 5000); // Check after 5 seconds
  }, []); // Remove activeSubscriptions dependency since we clear all before subscribing

  const unsubscribeFromAllTokens = useCallback(() => {
    setActiveSubscriptions(prevSubs => {
      console.log(`🔌 P&L Widget: Unsubscribing from ${prevSubs.size} token price feeds...`);
      
      prevSubs.forEach((unsubscribe, tokenMint) => {
        try {
          unsubscribe();
        } catch (error) {
          console.error(`❌ P&L Widget: Error unsubscribing from ${tokenMint}:`, error);
        }
      });
      
      console.log(`✅ P&L Widget: Unsubscribed from all token price feeds`);
      return new Map();
    });
    
    setRealtimePrices({});
  }, []);

  // 🔥 **ENHANCED TOKEN METADATA FETCHING**: Comprehensive metadata fetching with caching
  const fetchTokenMetadata = useCallback(async (walletAddress: string, tokenMints: string[]) => {
    if (!walletAddress || !tokenMints.length) return;
    
    setIsLoadingMetadata(true);
    console.log(`🏷️ P&L Widget: Fetching metadata for ${tokenMints.length} tokens from wallet ${walletAddress.slice(0, 8)}...`);
    
    // Check cache first and apply any cached metadata immediately
    const now = Date.now();
    const cacheTimeout = 5 * 60 * 1000; // 5 minutes
    const metadataMap: {[key: string]: TokenMetadata} = {};
    const uncachedTokens: string[] = [];
    
    tokenMints.forEach(mint => {
      const cached = metadataCache.get(mint);
      if (cached && (now - cached.timestamp) < cacheTimeout) {
        metadataMap[mint] = cached.metadata;
      } else {
        uncachedTokens.push(mint);
      }
    });
    
    // Apply cached metadata immediately
    if (Object.keys(metadataMap).length > 0) {
      console.log(`📋 P&L Widget: Applied ${Object.keys(metadataMap).length} cached token metadata`);
      setTokenMetadata(prev => ({
        ...prev,
        ...metadataMap
      }));
    }
    
    // If all tokens were cached, we're done
    if (uncachedTokens.length === 0) {
      setIsLoadingMetadata(false);
      return;
    }
    
    console.log(`🔄 P&L Widget: Fetching metadata for ${uncachedTokens.length} uncached tokens...`);
    
    try {
      // Strategy 1: Use the wallet API first (most efficient - gets all tokens in 1 call)
      console.log(`📡 P&L Widget: Trying wallet API for bulk metadata...`);
      const walletResponse = await getWalletTokensWithMetadata(walletAddress);
      
      if (walletResponse.status === 'success' && walletResponse.data?.tokens) {
        console.log(`✅ P&L Widget: Wallet API returned ${walletResponse.data.tokens.length} tokens`);
        
        // Process tokens from wallet API
        walletResponse.data.tokens.forEach((token: any) => {
          const metadata: TokenMetadata = {
            mint: token.mint,
            symbol: token.symbol || 'Unknown',
            name: token.name || 'Unknown Token',
            image: token.image || null,
            decimals: token.decimals || 9
          };
          
          // Only add if this token is in our uncached list
          if (uncachedTokens.includes(token.mint)) {
            metadataMap[token.mint] = metadata;
            // Cache the metadata
            metadataCache.set(token.mint, {
              metadata,
              timestamp: now
            });
          }
        });
        
        // Check which tokens we still need
        const missingTokens = uncachedTokens.filter(mint => !metadataMap[mint]);
        
        if (missingTokens.length > 0) {
          console.log(`🔍 P&L Widget: ${missingTokens.length} tokens missing from wallet API, trying individual calls...`);
          
          // Strategy 2: Individual calls for missing tokens (with rate limiting)
          if (missingTokens.length <= 10) {
            console.log(`🔄 P&L Widget: Fetching ${missingTokens.length} tokens individually with rate limiting...`);
            
            const individualPromises = missingTokens.map(async (mint, index) => {
              // Add delay to avoid rate limiting (200ms between requests)
              await new Promise(resolve => setTimeout(resolve, index * 200));
              
              try {
                const tokenResponse = await getTokenInfo(mint);
                if (tokenResponse.status === 'success' && tokenResponse.data?.token) {
                  const token = tokenResponse.data.token;
                  const metadata: TokenMetadata = {
                    mint,
                    symbol: token.symbol || 'Unknown',
                    name: token.name || 'Unknown Token',
                    image: token.image || null,
                    decimals: token.decimals || 9
                  };
                  metadataMap[mint] = metadata;
                  // Cache the metadata
                  metadataCache.set(mint, {
                    metadata,
                    timestamp: now
                  });
                } else {
                  // Fallback for tokens we can't fetch
                  metadataMap[mint] = {
                    mint,
                    symbol: mint.slice(0, 6),
                    name: 'Unknown Token',
                    image: null,
                    decimals: 9
                  };
                }
              } catch (error) {
                console.warn(`P&L Widget: Failed to fetch metadata for token ${mint}:`, error);
                metadataMap[mint] = {
                  mint,
                  symbol: mint.slice(0, 6),
                  name: 'Unknown Token',
                  image: null,
                  decimals: 9
                };
              }
            });
            
            await Promise.all(individualPromises);
          } else {
            // Too many missing tokens, create fallbacks
            console.log(`⚠️ P&L Widget: Too many missing tokens (${missingTokens.length}), creating fallbacks`);
            missingTokens.forEach(mint => {
              metadataMap[mint] = {
                mint,
                symbol: mint.slice(0, 6),
                name: 'Unknown Token',
                image: null,
                decimals: 9
              };
            });
          }
        }
        
        // Update token metadata state
        setTokenMetadata(prev => ({
          ...prev,
          ...metadataMap
        }));
        
        console.log(`✅ P&L Widget: Token metadata updated for ${Object.keys(metadataMap).length}/${uncachedTokens.length} new tokens (${tokenMints.length} total)`);
        
      } else {
        console.warn('⚠️ P&L Widget: Wallet API failed, using fallback approach');
        await createFallbackMetadata(uncachedTokens);
      }
      
    } catch (error) {
      console.error('❌ P&L Widget: Error fetching token metadata:', error);
      await createFallbackMetadata(uncachedTokens);
    } finally {
      setIsLoadingMetadata(false);
    }
  }, []);

  // Helper function to create fallback metadata
  const createFallbackMetadata = async (tokenMints: string[]) => {
    console.log(`🔄 P&L Widget: Creating fallback metadata for ${tokenMints.length} tokens`);
    
    const fallbackMetadata: Record<string, TokenMetadata> = {};
    tokenMints.forEach(mint => {
      fallbackMetadata[mint] = {
        mint,
        symbol: mint.slice(0, 6),
        name: 'Unknown Token',
        image: null,
        decimals: 9
      };
    });
    
    setTokenMetadata(prev => ({
      ...prev,
      ...fallbackMetadata
    }));
  };

  // Load trading wallets and balances (same as Active Positions)
  useEffect(() => {
    const loadData = async () => {
      if (!walletUser.walletAddress) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        console.log(`📊 P&L: Loading trading wallets for ${walletUser.walletAddress.slice(0, 8)}...`);
        
        // Fetch trading wallets
        const wallets = await getUserTradingWallets(walletUser.walletAddress);
        setTradingWallets(wallets);
        console.log(`✅ P&L: Found ${wallets.length} trading wallets`);

        // Fetch user profile to get active trading wallet
        const profile = await getUserProfile(walletUser.walletAddress);
        setActiveTradingWallet(profile?.active_trade_wallet || null);

        // Fetch balances for all wallets
        const balances = new Map<string, number>();
        
        // Note: For P&L, we don't need SOL balances, just wallet info
        wallets.forEach(wallet => {
          balances.set(wallet.public_key, 0); // Placeholder balance
        });

        setWalletBalances(Object.fromEntries(balances));

        // Build available wallets list (only trading wallets for P&L)
        const walletOptions: WalletOption[] = wallets.map(wallet => ({
          address: wallet.public_key,
          name: wallet.name,
          type: 'trading' as const,
          isActive: wallet.public_key === profile?.active_trade_wallet,
          balance: balances.get(wallet.public_key) || 0
        })).sort((a, b) => {
          // Sort by active wallet first, then by name
          if (a.isActive && !b.isActive) return -1;
          if (!a.isActive && b.isActive) return 1;
          return a.name.localeCompare(b.name);
        });

        setAvailableWallets(walletOptions);

        // Set default viewing wallet (prioritize active trading wallet, then first wallet)
        const defaultWallet = profile?.active_trade_wallet || (walletOptions.length > 0 ? walletOptions[0].address : null);
        
        console.log(`📊 P&L: Setting default viewing wallet:`, {
          activeWallet: profile?.active_trade_wallet?.slice(0, 8) + '...' || 'none',
          firstWallet: walletOptions.length > 0 ? walletOptions[0].address.slice(0, 8) + '...' : 'none',
          selectedDefault: defaultWallet?.slice(0, 8) + '...' || 'none'
        });

        if (defaultWallet) {
          setCurrentViewingWallet(defaultWallet);
        }

      } catch (err) {
        console.error('Error loading trading wallets:', err);
        setError('Failed to load trading wallets');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [walletUser.walletAddress]);

  // Function to copy token mint address to clipboard
  const copyTokenAddress = (e: React.MouseEvent, mint: string) => {
    e.stopPropagation();
    e.preventDefault();
    
    navigator.clipboard.writeText(mint);
    setCopiedTokenMint(mint);
    setTimeout(() => setCopiedTokenMint(null), 2000); // Reset after 2 seconds
  };

  // Hidden tokens management functions
  const loadHiddenTokens = useCallback(() => {
    try {
      const stored = localStorage.getItem('ekko-hidden-tokens');
      if (stored) {
        const hiddenArray = JSON.parse(stored);
        setHiddenTokens(new Set(hiddenArray));
      }
    } catch (error) {
      console.error('Error loading hidden tokens:', error);
    }
  }, []);

  const saveHiddenTokens = useCallback((tokens: Set<string>) => {
    try {
      localStorage.setItem('ekko-hidden-tokens', JSON.stringify(Array.from(tokens)));
    } catch (error) {
      console.error('Error saving hidden tokens:', error);
    }
  }, []);

  const hideToken = useCallback((tokenMint: string) => {
    setHiddenTokens(prev => {
      const newHidden = new Set(prev);
      newHidden.add(tokenMint);
      saveHiddenTokens(newHidden);
      return newHidden;
    });
  }, [saveHiddenTokens]);

  const unhideToken = useCallback((tokenMint: string) => {
    setHiddenTokens(prev => {
      const newHidden = new Set(prev);
      newHidden.delete(tokenMint);
      saveHiddenTokens(newHidden);
      return newHidden;
    });
  }, [saveHiddenTokens]);

  // Load hidden tokens on component mount
  useEffect(() => {
    loadHiddenTokens();
  }, [loadHiddenTokens]);

  // 🔥 **INITIAL DATA FETCHING**: Fetch PnL data when viewing wallet changes
  useEffect(() => {
    const fetchPnLData = async () => {
      if (!currentViewingWallet || isLoading) return;
      
      console.log(`🔄 P&L: Viewing wallet changed to: ${currentViewingWallet.slice(0, 8)}... - Fetching P&L data`);
      
      // Immediately unsubscribe from all current tokens when switching wallets
      unsubscribeFromAllTokens();
      
      setError(null);
      
      try {
        // Use the service instead of direct API client
        const result = await getWalletProfitAndLoss(currentViewingWallet, true);
        
        if (result.status === 'success' && result.data) {
          setPnlData(result.data);
          
          // Extract token entries for sorting
          if (result.data.tokens) {
            const entries = Object.entries(result.data.tokens);
            setTokenEntries(entries);
            
            // Get list of token mints to fetch metadata for
            const mints = entries.map(([mint]) => mint);
            
            // 🔥 **ENHANCED METADATA AND WEBSOCKET SETUP**
            if (mints.length > 0) {
              // Use our new approach to fetch token metadata for ALL tokens, including zero balance
              fetchTokenMetadata(currentViewingWallet, mints).catch(err => {
                console.error('Failed to fetch token metadata:', err);
              });
              
              // Subscribe to real-time price updates for tokens with holdings only
              const tokensWithHoldings = entries
                .filter(([_, tokenPnL]) => (tokenPnL as any).holding > 0)
                .map(([mint, _]) => mint);
              
              if (tokensWithHoldings.length > 0) {
                subscribeToTokenPrices(tokensWithHoldings).catch(err => {
                  console.error('Failed to subscribe to token prices:', err);
                });
              }
            }
          } else {
            // Handle case where data.tokens is undefined
            setTokenEntries([]);
          }
        } else {
          setError(result.error || 'Failed to fetch profit and loss data.');
          setTokenEntries([]);
        }
      } catch (err) {
        console.error('Error fetching PnL data:', err);
        
        // Provide more specific error messages based on error type
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to fetch profit and loss data. Please try again later.');
        }
      }
    };

    fetchPnLData();
  }, [currentViewingWallet, fetchTokenMetadata, subscribeToTokenPrices]);

  // 🔥 **BACKGROUND POLLING**: Set up background P&L polling every 5 seconds (like Active Positions)
  useEffect(() => {
    if (!currentViewingWallet || isLoading) return;

    console.log(`⏰ P&L Widget: Setting up background P&L polling for wallet ${currentViewingWallet.slice(0, 8)}... (every 5 seconds)`);
    
    const pollInterval = setInterval(async () => {
      if (!currentViewingWallet || isLoadingRef.current) return;
      
      try {
        console.log(`🔄 P&L Widget: Background P&L refresh for wallet ${currentViewingWallet.slice(0, 8)}...`);
        
        // Temporarily set loading ref to prevent duplicate calls
        isLoadingRef.current = true;
        
        const apiKey = import.meta.env.VITE_SOLANA_TRACKER_API_KEY;
        if (!apiKey) {
          console.warn('⚠️ P&L Widget: Background refresh skipped: API key not found');
          return;
        }
        
        const response = await fetch(`https://data.solanatracker.io/pnl/${currentViewingWallet}`, {
          method: 'GET',
          headers: {
            'x-api-key': apiKey,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          console.warn(`⚠️ P&L Widget: Background P&L refresh failed: ${response.status} ${response.statusText}`);
          return;
        }
        
        const pnlData = await response.json();
        
        // Silently update the PnL state
        setPnlData(pnlData);
        
        // Update token entries
        if (pnlData.tokens) {
          const entries = Object.entries(pnlData.tokens);
          setTokenEntries(entries);
        }
        
        console.log(`✅ P&L Widget: Background P&L refresh completed for wallet ${currentViewingWallet.slice(0, 8)}...`, {
          tokensCount: Object.keys(pnlData.tokens || {}).length,
          tokensWithHoldings: Object.entries(pnlData.tokens || {}).filter(([_, token]) => (token as any).holding > 0).length
        });
        
      } catch (error) {
        console.warn('⚠️ P&L Widget: Background P&L refresh error:', error);
      } finally {
        isLoadingRef.current = false;
      }
    }, 5000); // Poll every 5 seconds

    // Store interval reference for cleanup
    backgroundPollIntervalRef.current = pollInterval;

    // Cleanup interval on unmount or wallet change
    return () => {
      console.log(`⏰ P&L Widget: Cleaning up background P&L polling for wallet ${currentViewingWallet.slice(0, 8)}...`);
      if (backgroundPollIntervalRef.current) {
        clearInterval(backgroundPollIntervalRef.current);
        backgroundPollIntervalRef.current = null;
      }
    };
  }, [currentViewingWallet, isLoading]);

  // 🔥 **WEBSOCKET CLEANUP**: Cleanup WebSocket subscriptions on unmount
  useEffect(() => {
    return () => {
      console.log('🔌 P&L Widget: Component unmounting, cleaning up WebSocket subscriptions');
      unsubscribeFromAllTokens();
      
      // Also cleanup background polling
      if (backgroundPollIntervalRef.current) {
        clearInterval(backgroundPollIntervalRef.current);
        backgroundPollIntervalRef.current = null;
      }
    };
  }, []);

  // Set up automatic refresh interval
  useEffect(() => {
    if (currentViewingWallet) {
      // Clear any existing interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      
      // Set up a new interval to refresh data every 30 seconds
      refreshIntervalRef.current = setInterval(() => {
        console.log(`Auto-refreshing PnL data for ${activeTradingWallet}`);
        setIsAutoRefreshing(true);
        
        // Use the refresh function
        handleRefresh().finally(() => {
          setIsAutoRefreshing(false);
        });
      }, 30000); // 30 seconds
      
      // Log that auto-refresh is enabled
      console.log(`PnL auto-refresh enabled for wallet ${activeTradingWallet}`);
    }
    
    // Clean up interval on unmount or wallet change
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [activeTradingWallet]);

  // Format currency with appropriate precision
  const formatCurrency = (amount: number): string => {
    if (Math.abs(amount) >= 1_000_000) {
      return `$${(amount / 1_000_000).toFixed(2)}M`;
    } else if (Math.abs(amount) >= 1_000) {
      return `$${(amount / 1_000).toFixed(2)}K`;
    } else if (Math.abs(amount) < 0.01 && amount !== 0) {
      return `$${amount.toFixed(6)}`;
    } else {
      return `$${amount.toFixed(2)}`;
    }
  };

  // Format P&L values (same as Active Positions)
  const formatPnL = (value: number) => {
    const formatted = formatCurrency(value);
    return value >= 0 ? `+${formatted}` : formatted;
  };

  // Format percentage
  const formatPercentage = (percentage: number): string => {
    if (percentage === Infinity || isNaN(percentage)) {
      return '-%';
    }
    return `${percentage > 0 ? '+' : ''}${percentage.toFixed(2)}%`;
  };

  // Sort token entries
  const sortedTokenEntries = useMemo(() => {
    if (!tokenEntries.length) return [];
    
    // First filter by hidden/visible status
    let filteredEntries = showingHidden
      ? tokenEntries.filter(([tokenMint]) => hiddenTokens.has(tokenMint))
      : tokenEntries.filter(([tokenMint]) => !hiddenTokens.has(tokenMint));
    
    // Then filter by active positions if enabled (only for visible tokens)
    if (!showingHidden && showActivePositions) {
      filteredEntries = filteredEntries.filter(([_, token]) => (token as TokenData).holding > 0);
    }
    
    // Then filter out zero balance tokens if showZeroBalance is false (only for visible tokens)
    if (!showingHidden && !showZeroBalance) {
      filteredEntries = filteredEntries.filter(([_, token]) => (token as TokenData).holding > 0);
    }
    
    // Sort by the current sort option
    const sortFn = (a: [string, any], b: [string, any]) => {
      const valueA = a[1][sortOption] || 0;
      const valueB = b[1][sortOption] || 0;
      return sortDirection === 'desc' ? valueB - valueA : valueA - valueB;
    };
    
    return filteredEntries.sort(sortFn);
  }, [tokenEntries, sortOption, sortDirection, showZeroBalance, showActivePositions, hiddenTokens, showingHidden]);

  // Handle sort option change
  const handleSort = (option: string) => {
    if (sortOption === option) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortOption(option);
      setSortDirection('desc');
    }
  };

  // Update handleRefresh to return a promise so we can track when it completes
  const handleRefresh = useCallback(async () => {
    if (!currentViewingWallet) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Use the service instead of direct API client
      const result = await getWalletProfitAndLoss(currentViewingWallet, true);
      
      if (result.status === 'success' && result.data) {
        setPnlData(result.data);
        
        // Extract token entries for sorting
        if (result.data.tokens) {
          const entries = Object.entries(result.data.tokens);
          setTokenEntries(entries);
          
          // Get list of token mints to fetch metadata for
          const mints = entries.map(([mint]) => mint);
          
          // Use our new approach to fetch token metadata
          if (mints.length > 0) {
            fetchTokenMetadata(currentViewingWallet, mints).catch(err => {
              console.error('Failed to fetch token metadata during refresh:', err);
            });
          }
        } else {
          setTokenEntries([]);
        }
      } else {
        setError(result.error || 'Failed to refresh profit and loss data.');
        setTokenEntries([]);
      }
    } catch (err) {
      console.error('Error refreshing PnL data:', err);
      
      // Provide more specific error messages based on error type
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to refresh profit and loss data. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentViewingWallet, fetchTokenMetadata]);

  // Clear synchronizing state when data is loaded
  useEffect(() => {
    if (pnlData && isSynchronizing) {
      const timer = setTimeout(() => {
        setIsSynchronizing(false);
      }, 500); // Small delay to show the animation
      
      return () => clearTimeout(timer);
    }
  }, [pnlData, isSynchronizing]);

  const content = (
    <div className="w-full h-full flex flex-col bg-white overflow-hidden rounded-xl border border-gray-200 shadow-sm" data-widget-type="profit-and-loss">
      {/* Custom scrollbar color override for this widget */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          background: #f8f9fa;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e9ecef;
          border-radius: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #dee2e6;
        }
        .custom-scrollbar {
          scrollbar-color: #e9ecef #f8f9fa;
          scrollbar-width: thin;
        }
        
        /* Range slider styling */
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #24c35b;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .slider::-webkit-slider-thumb:hover {
          background: #22b55a;
          transform: scale(1.1);
        }
        
        .slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #24c35b;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .slider::-moz-range-thumb:hover {
          background: #22b55a;
          transform: scale(1.1);
        }
      `}</style>
      {/* Summary Stats */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <RefreshCcw size={20} className="animate-spin text-[#24c35b] mr-2" />
          <span className="text-gray-600">Loading profit and loss data...</span>
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-center">
            <div className="text-red-600 text-sm font-medium mb-2">Error Loading Data</div>
            <div className="text-red-500 text-xs">{error}</div>
            <button
              onClick={() => {
                setError(null);
                handleRefresh();
              }}
              className="mt-3 px-3 py-1.5 bg-[#24c35b] text-white text-xs rounded-lg hover:bg-[#20a855] transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      ) : pnlData ? (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-3 p-3">
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs text-gray-600 mb-1 flex items-center justify-between">
                <span>Total P&L</span>
                {summaryStats?.hasRealtimeData && (
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse mr-1" />
                    <span className="text-green-500 text-xs">Live</span>
                  </div>
                )}
              </div>
              <div className={`text-lg font-semibold ${summaryStats?.total >= 0 ? 'text-[#24c35b]' : 'text-red-500'} ${summaryStats?.hasRealtimeData ? 'font-bold' : ''}`}>
                {formatCurrency(summaryStats?.total || 0)}
              </div>
            </div>
            
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs text-gray-600 mb-1">Total Invested</div>
              <div className="text-lg font-semibold text-black">
                {formatCurrency(summaryStats?.totalInvested || 0)}
              </div>
            </div>
            
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs text-gray-600 mb-1">Realized</div>
              <div className={`text-sm font-medium ${summaryStats?.realized >= 0 ? 'text-[#24c35b]' : 'text-red-500'}`}>
                {formatCurrency(summaryStats?.realized || 0)}
              </div>
            </div>
            
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs text-gray-600 mb-1">Unrealized</div>
              <div className={`text-sm font-medium ${summaryStats?.unrealized >= 0 ? 'text-[#24c35b]' : 'text-red-500'}`}>
                {formatCurrency(summaryStats?.unrealized || 0)}
              </div>
            </div>
          </div>
          
          {/* Performance Stats & Token Header - Combined */}
          <div className="border-b border-gray-200">
            {/* Performance Stats */}
            <div className="flex justify-between px-3 py-2">
              <div className="flex items-center">
                <TrendingUp size={14} className="text-[#24c35b] mr-1" />
                <span className="text-xs text-black font-medium">Performance</span>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center">
                  <span className="text-xs text-[#24c35b] mr-1 font-medium">{summaryStats?.winPercentage.toFixed(0)}%</span>
                  <span className="text-xs text-gray-600">Win</span>
                </div>
                
                <div className="flex items-center">
                  <span className="text-xs text-red-500 mr-1 font-medium">{summaryStats?.lossPercentage.toFixed(0)}%</span>
                  <span className="text-xs text-gray-600">Loss</span>
                </div>
                
                <div className="flex items-center">
                  <span className="text-xs text-black mr-1 font-medium">{summaryStats?.totalWins}/{summaryStats?.totalWins + summaryStats?.totalLosses}</span>
                  <span className="text-xs text-gray-600">W/L</span>
                </div>
              </div>
            </div>
            
            {/* Tabs for Visible/Hidden tokens */}
            <div className="px-3 pb-2">
              <div className="flex items-center gap-2 mb-2">
                <button
                  onClick={() => setShowingHidden(false)}
                  className={`text-xs px-3 py-1.5 rounded-lg ${
                    !showingHidden 
                      ? 'bg-[#24c35b] text-white shadow-sm' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Visible
                </button>
                <button
                  onClick={() => setShowingHidden(true)}
                  className={`text-xs px-3 py-1.5 rounded-lg ${
                    showingHidden 
                      ? 'bg-[#24c35b] text-white shadow-sm' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Hidden ({hiddenTokens.size})
                </button>
                
                {/* Only show filters when viewing visible tokens */}
                {!showingHidden && (
                  <>
                    <button
                      onClick={() => setShowActivePositions(!showActivePositions)}
                      className={`text-xs px-3 py-1.5 rounded-lg ${
                        showActivePositions 
                          ? 'bg-[#24c35b] text-white shadow-sm' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {showActivePositions ? 'Active P&L' : 'All Time P&L'}
                    </button>
                    <button
                      onClick={() => setShowZeroBalance(!showZeroBalance)}
                      className={`text-xs px-2 py-1.5 rounded-lg ${
                        showZeroBalance 
                          ? 'bg-[#24c35b] text-white shadow-sm' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {showZeroBalance ? 'Hide Zero Balance' : 'Show Zero Balance'}
                    </button>
                  </>
                )}
              </div>
              
              {/* Header Row */}
              <div className="bg-gray-50 rounded-lg p-2">
                <div className="grid grid-cols-12 text-xs text-gray-600 font-medium">
                  <div className="col-span-3 flex items-center cursor-pointer" onClick={() => handleSort('token')}>
                    Token
                    {sortOption === 'token' && (
                      sortDirection === 'desc' ? 
                        <ChevronDown size={12} className="ml-1" /> : 
                        <ChevronUp size={12} className="ml-1" />
                    )}
                  </div>
                  <div className="col-span-2 text-right cursor-pointer" onClick={() => handleSort('current_value')}>
                    Value
                    {sortOption === 'current_value' && (
                      sortDirection === 'desc' ? 
                        <ChevronDown size={12} className="ml-1 inline" /> : 
                        <ChevronUp size={12} className="ml-1 inline" />
                    )}
                  </div>
                  <div className="col-span-2 text-right cursor-pointer" onClick={() => handleSort('total')}>
                    P&L
                    {sortOption === 'total' && (
                      sortDirection === 'desc' ? 
                        <ChevronDown size={12} className="ml-1 inline" /> : 
                        <ChevronUp size={12} className="ml-1 inline" />
                    )}
                  </div>
                  <div className="col-span-2 text-right cursor-pointer" onClick={() => handleSort('roi')}>
                    ROI
                    {sortOption === 'roi' && (
                      sortDirection === 'desc' ? 
                        <ChevronDown size={12} className="ml-1 inline" /> : 
                        <ChevronUp size={12} className="ml-1 inline" />
                    )}
                  </div>
                  <div className="col-span-3 text-center">
                    Actions
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Token List */}
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {sortedTokenEntries.length > 0 ? (
                sortedTokenEntries
                .map(([tokenMint, tokenPnL]) => {
                  const tokenMetadataInfo = tokenMetadata[tokenMint];
                  const realTimePnL = calculateRealTimePnL(tokenMint, tokenPnL as TokenData);

                  // Calculate ROI using real-time values
                  const invested = (tokenPnL as TokenData).total_invested;
                  const roi = invested > 0 ? ((realTimePnL.currentValue - invested) / invested) * 100 : 0;

                  const tokenAddress = tokenMint;

                  return (
                    <div 
                      key={tokenMint} 
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors p-3"
                      onClick={() => {
                        if (!showingHidden) {
                          const tokenData = {
                            mint: tokenMint,
                            symbol: tokenMetadataInfo?.symbol || 'UNKNOWN',
                            name: tokenMetadataInfo?.name || 'Unknown Token',
                            image_url: tokenMetadataInfo?.image || null
                          };
                          
                          // Dispatch event for token selection
                          const event = new CustomEvent('ekko:token_selected', {
                            detail: { token: tokenData }
                          });
                          window.dispatchEvent(event);
                          
                          // Dispatch event for bundle scanner
                          const bundleScannerEvent = new CustomEvent('ekko:bundle_scanner_update', {
                            detail: { token: tokenData, timestamp: Date.now() }
                          });
                          window.dispatchEvent(bundleScannerEvent);
                        }
                      }}
                    >
                      <div className="grid grid-cols-12 text-sm items-center">
                        {/* Token Info */}
                        <div className="col-span-3 flex items-center min-w-0">
                          <div className="flex items-center">
                            {tokenMetadataInfo?.image ? (
                              <img 
                                src={tokenMetadataInfo.image} 
                                alt={tokenMetadataInfo.symbol || 'Token'} 
                                className="w-6 h-6 rounded-full mr-2 flex-shrink-0"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-6 h-6 bg-gray-200 rounded-full mr-2 flex-shrink-0 flex items-center justify-center">
                                <span className="text-xs text-gray-500 font-medium">
                                  {tokenMetadataInfo?.symbol?.[0] || '?'}
                                </span>
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="font-medium text-black text-xs truncate">
                                {tokenMetadataInfo?.symbol || `${tokenMint.slice(0, 6)}...`}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {(tokenPnL as TokenData).holding.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Value */}
                        <div className="col-span-2 flex flex-col items-end justify-center">
                          <div className={`text-xs ${realTimePnL.isRealTime ? 'text-green-500 font-medium' : 'text-black'}`}>
                            {formatCurrency(realTimePnL.currentValue)}
                          </div>
                          {realTimePnL.isRealTime && (
                            <div className="text-xs text-gray-500">
                              Live
                            </div>
                          )}
                        </div>
                        
                        {/* P&L */}
                        <div className="col-span-2 flex flex-col items-end justify-center">
                          <div className={`text-xs ${realTimePnL.total >= 0 ? 'text-[#24c35b]' : 'text-red-500'} ${realTimePnL.isRealTime ? 'font-medium' : ''}`}>
                            {formatCurrency(realTimePnL.total)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {(tokenPnL as TokenData).realized !== 0 && (
                              <span className={(tokenPnL as TokenData).realized >= 0 ? 'text-[#24c35b]' : 'text-red-500'}>
                                R: {formatCurrency((tokenPnL as TokenData).realized)}
                              </span>
                            )}
                            {realTimePnL.isRealTime && realTimePnL.unrealized !== (tokenPnL as TokenData).unrealized && (
                              <span className={`ml-1 ${realTimePnL.unrealized >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                U: {formatCurrency(realTimePnL.unrealized)}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* ROI */}
                        <div className="col-span-2 flex flex-col items-end justify-center">
                          <div className={`text-xs ${roi >= 0 ? 'text-[#24c35b]' : 'text-red-500'} flex items-center ${realTimePnL.isRealTime ? 'font-medium' : ''}`}>
                            {roi >= 0 ? <ArrowUp size={10} className="mr-1" /> : <ArrowDown size={10} className="mr-1" />}
                            {formatPercentage(roi)}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="col-span-3 flex items-center justify-center gap-1">
                          {/* Hide/Unhide Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (showingHidden) {
                                unhideToken(tokenAddress);
                              } else {
                                hideToken(tokenAddress);
                              }
                            }}
                            className={`p-1.5 rounded-lg border transition-all group ${
                              showingHidden
                                ? 'bg-blue-50 hover:bg-blue-100 border-blue-200 hover:border-blue-300'
                                : 'bg-gray-50 hover:bg-gray-100 border-gray-200 hover:border-gray-300'
                            }`}
                            title={showingHidden ? 'Unhide token' : 'Hide token'}
                          >
                            {showingHidden ? (
                              <Eye size={12} className="text-blue-500 group-hover:text-blue-600" />
                            ) : (
                              <EyeOff size={12} className="text-gray-500 group-hover:text-gray-600" />
                            )}
                          </button>
                          
                          {/* Share Button - only show for visible tokens */}
                          {!showingHidden && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShareToken(tokenMint, tokenPnL);
                              }}
                              className="p-1.5 rounded-lg bg-[#24c35b]/10 hover:bg-[#24c35b]/20 border border-[#24c35b]/20 hover:border-[#24c35b]/40 transition-all group"
                              title="Share token performance"
                            >
                              <Share2 size={12} className="text-[#24c35b] group-hover:text-white" />
                            </button>
                          )}
                          
                          {/* Copy Address Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyTokenAddress(e, tokenAddress);
                            }}
                            className="p-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 transition-all group"
                            title="Copy token address"
                          >
                            {copiedTokenMint === tokenAddress ? (
                              <Check size={12} className="text-green-500" />
                            ) : (
                              <Copy size={12} className="text-gray-500 group-hover:text-gray-600" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
            ) : (
              <div className="p-8 text-center text-gray-500">
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader size={20} className="animate-spin text-[#24c35b]" />
                    <span>Loading token data...</span>
                  </div>
                ) : (
                  <>
                    <div className="text-lg mb-2">📭 No Active Positions</div>
                    <div className="text-sm">No tokens with current holdings found</div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-gray-400">
          <span>No data available</span>
        </div>
      )}
      
      {/* 🔥 **SHARE MODAL**: Complete share modal system */}
      {showShareModal && shareData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]" onClick={() => setShowShareModal(false)}>
          <div className="bg-white border border-gray-200 rounded-2xl w-[1400px] max-w-[98vw] max-h-[95vh] overflow-hidden shadow-xl" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#24c35b]"></div>
                <Share2 size={18} className="text-[#24c35b]" />
                <h2 className="text-base font-semibold text-black">
                  Share {shareData?.type === 'token' ? `$${shareData?.tokenSymbol || ''}` : 'Portfolio'} Performance
                </h2>
              </div>
              <button
                onClick={() => setShowShareModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X size={18} className="text-gray-500" />
              </button>
            </div>

            <div className="flex h-[700px]">
              {/* Left Panel - Customization Options */}
              <div className="w-1/3 p-4 border-r border-[#24c35b]/10 overflow-y-auto custom-scrollbar">
                <h3 className="text-lg font-semibold text-white mb-4">Customize Your Card</h3>
                
                <div className="space-y-6">
                  {/* Custom Text */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300 block">Custom Message</label>
                    <textarea
                      value={shareCustomization.customText}
                      onChange={(e) => setShareCustomization(prev => ({ ...prev, customText: e.target.value }))}
                      className="w-full bg-gray-600/10 border border-gray-600/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#24c35b]/50 focus:ring-1 focus:ring-[#24c35b]/20 resize-none"
                      rows={3}
                      placeholder="Add your custom message..."
                    />
                  </div>

                  {/* Background Image Upload */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-300 block">Background Image</label>
                    
                    {/* Global Background Upload */}
                    <div className="space-y-2">
                      <label className="text-xs text-gray-400 block">Custom Background</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                setShareCustomization(prev => ({ 
                                  ...prev, 
                                  customBackgroundImage: event.target?.result as string,
                                  backgroundStyle: 'custom',
                                  useTokenAsBackground: false
                                }));
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="hidden"
                          id="background-upload"
                        />
                        <label
                          htmlFor="background-upload"
                          className="flex-1 px-3 py-2 bg-gray-600/10 border border-gray-600/30 rounded-lg text-white text-sm cursor-pointer hover:bg-gray-600/20 transition-colors flex items-center justify-center gap-2"
                        >
                          <Upload size={14} />
                          Choose Background
                        </label>
                        {shareCustomization.customBackgroundImage && (
                          <button
                            onClick={() => setShareCustomization(prev => ({ 
                              ...prev, 
                              customBackgroundImage: null,
                              backgroundStyle: 'gradient'
                            }))}
                            className="px-2 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/30 transition-colors"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                      {shareCustomization.customBackgroundImage && (
                        <div className="mt-2">
                          <img
                            src={shareCustomization.customBackgroundImage}
                            alt="Background preview"
                            className="w-full h-20 object-cover rounded-lg border border-gray-600/30"
                          />
                        </div>
                      )}
                    </div>

                    {/* Token Background Toggle - only show for token shares */}
                    {shareData?.type === 'token' && shareData?.tokenImage && (
                      <div className="space-y-2">
                        <label className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">Use token as background</span>
                          <button
                            onClick={() => setShareCustomization(prev => ({ 
                              ...prev, 
                              useTokenAsBackground: !prev.useTokenAsBackground,
                              backgroundStyle: !prev.useTokenAsBackground ? 'token' : 'gradient',
                              customBackgroundImage: !prev.useTokenAsBackground ? null : prev.customBackgroundImage
                            }))}
                            className={`w-10 h-5 rounded-full transition-colors ${
                              shareCustomization.useTokenAsBackground ? 'bg-[#24c35b]' : 'bg-gray-600/30'
                            }`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                              shareCustomization.useTokenAsBackground ? 'translate-x-5' : 'translate-x-0.5'
                            }`} />
                          </button>
                        </label>
                        {shareCustomization.useTokenAsBackground && (
                          <div className="mt-2">
                            <img
                              src={shareData.tokenImage}
                              alt="Token background preview"
                              className="w-full h-20 object-cover rounded-lg border border-gray-600/30"
                            />
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Background Opacity Slider */}
                    {(shareCustomization.useTokenAsBackground || shareCustomization.customBackgroundImage) && (
                      <div className="space-y-2">
                        <label className="text-xs text-gray-400 block">
                          Background Opacity: {shareCustomization.tokenBackgroundOpacity}%
                        </label>
                        <input
                          type="range"
                          min="5"
                          max="80"
                          value={shareCustomization.tokenBackgroundOpacity}
                          onChange={(e) => setShareCustomization(prev => ({ 
                            ...prev, 
                            tokenBackgroundOpacity: parseInt(e.target.value) 
                          }))}
                          className="w-full h-2 bg-gray-600/30 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>5%</span>
                          <span>80%</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Color Scheme & Currency */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-300 mb-2 block">Color Scheme</label>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { key: 'green', name: 'Green', color: '#24c35b' },
                            { key: 'blue', name: 'Blue', color: '#3b82f6' },
                            { key: 'purple', name: 'Purple', color: '#8b5cf6' },
                            { key: 'gold', name: 'Gold', color: '#f59e0b' }
                          ].map(scheme => (
                            <button
                              key={scheme.key}
                              onClick={() => setShareCustomization(prev => ({ ...prev, colorScheme: scheme.key as any }))}
                              className={`px-2 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                                shareCustomization.colorScheme === scheme.key
                                  ? 'border-[#24c35b] bg-[#24c35b]/20 text-[#24c35b]'
                                  : 'border-gray-600/30 bg-gray-600/10 text-gray-400 hover:border-[#24c35b]/40 hover:text-gray-300'
                              }`}
                            >
                              <div className="w-3 h-3 rounded-full mx-auto mb-1" style={{ backgroundColor: scheme.color }}></div>
                              {scheme.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-300 mb-2 block">Currency</label>
                        <div className="grid grid-cols-1 gap-2">
                          <button
                            onClick={() => setShareCustomization(prev => ({ ...prev, currencyDisplay: 'SOL' }))}
                            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                              shareCustomization.currencyDisplay === 'SOL'
                                ? 'border-[#24c35b] bg-[#24c35b]/20 text-[#24c35b]'
                                : 'border-gray-600/30 bg-gray-600/10 text-gray-400 hover:border-[#24c35b]/40 hover:text-gray-300'
                            }`}
                          >
                            SOL
                          </button>
                          <button
                            onClick={() => setShareCustomization(prev => ({ ...prev, currencyDisplay: 'USD' }))}
                            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                              shareCustomization.currencyDisplay === 'USD'
                                ? 'border-[#24c35b] bg-[#24c35b]/20 text-[#24c35b]'
                                : 'border-gray-600/30 bg-gray-600/10 text-gray-400 hover:border-[#24c35b]/40 hover:text-gray-300'
                            }`}
                          >
                            USD
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Display Options */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-300 block">Display Options</label>
                    <div className="space-y-3">
                      <label className="flex items-center justify-between">
                        <span className="text-sm text-black">Show exact amounts</span>
                        <button
                          onClick={() => setShareCustomization(prev => ({ ...prev, showExactAmounts: !prev.showExactAmounts }))}
                          className={`w-10 h-5 rounded-full transition-colors ${
                            shareCustomization.showExactAmounts ? 'bg-[#24c35b]' : 'bg-gray-600/30'
                          }`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            shareCustomization.showExactAmounts ? 'translate-x-5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </label>

                      <label className="flex items-center justify-between">
                        <span className="text-sm text-black">Show ROI</span>
                        <button
                          onClick={() => setShareCustomization(prev => ({ ...prev, showROI: !prev.showROI }))}
                          className={`w-10 h-5 rounded-full transition-colors ${
                            shareCustomization.showROI ? 'bg-[#24c35b]' : 'bg-gray-600/30'
                          }`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            shareCustomization.showROI ? 'translate-x-5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </label>

                      <label className="flex items-center justify-between">
                        <span className="text-sm text-white">Show invested</span>
                        <button
                          onClick={() => setShareCustomization(prev => ({ ...prev, showInvested: !prev.showInvested }))}
                          className={`w-10 h-5 rounded-full transition-colors ${
                            shareCustomization.showInvested ? 'bg-[#24c35b]' : 'bg-gray-600/30'
                          }`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            shareCustomization.showInvested ? 'translate-x-5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </label>

                      <label className="flex items-center justify-between">
                        <span className="text-sm text-white">Show current value</span>
                        <button
                          onClick={() => setShareCustomization(prev => ({ ...prev, showCurrentValue: !prev.showCurrentValue }))}
                          className={`w-10 h-5 rounded-full transition-colors ${
                            shareCustomization.showCurrentValue ? 'bg-[#24c35b]' : 'bg-gray-600/30'
                          }`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            shareCustomization.showCurrentValue ? 'translate-x-5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </label>

                      {/* Token Image Toggle - only show for token shares */}
                      {shareData?.type === 'token' && shareData?.tokenImage && (
                        <label className="flex items-center justify-between">
                          <span className="text-sm text-white">Show token image</span>
                          <button
                            onClick={() => setShareCustomization(prev => ({ ...prev, showTokenImage: !prev.showTokenImage }))}
                            className={`w-10 h-5 rounded-full transition-colors ${
                              shareCustomization.showTokenImage ? 'bg-[#24c35b]' : 'bg-gray-600/30'
                            }`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                              shareCustomization.showTokenImage ? 'translate-x-5' : 'translate-x-0.5'
                            }`} />
                          </button>
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right Panel - Preview and Actions */}
              <div className="flex-1 p-4 flex flex-col">
                <div className="flex-1 flex items-center justify-center bg-gray-900/20 rounded-lg border border-gray-600/20 mb-4 min-h-[500px]">
                  {generatedCardUrl ? (
                    <img
                      src={generatedCardUrl}
                      alt="Share card preview"
                      className="max-w-full max-h-full object-contain rounded shadow-lg"
                    />
                  ) : (
                    <div className="text-center text-gray-400">
                      <div className="text-xl mb-3">📸 Preview</div>
                      <div className="text-sm mb-4">Generate card to see preview</div>
                      <div className="text-xs text-gray-500">
                        Customize your settings and click "Generate Card"
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3">
                  {generatedCardUrl ? (
                    <>
                      <button
                        onClick={downloadCard}
                        className="flex-1 bg-gray-600/20 hover:bg-gray-600/30 text-white py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 border border-gray-600/30"
                      >
                        <Download size={18} />
                        Download
                      </button>
                      
                      <button
                        onClick={shareToTwitter}
                        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <XLogo size={18} />
                        Tweet
                      </button>
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center py-3 px-6 text-gray-400">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-[#24c35b] border-t-transparent rounded-full animate-spin"></div>
                        <span>Generating card...</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // If this is being rendered as a widget, return just the content styled like AdvancedAutoTrader
  if (isWidget) {
    return (
      <div className="absolute inset-0 m-0 p-0 overflow-hidden bg-white flex flex-col rounded-xl border border-gray-200 shadow-sm">
        {/* Header with wallet toggle (same as Active Positions) */}
        <div className="p-3 border-b border-gray-200 bg-white flex items-center justify-between rounded-t-xl">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-[#24c35b] rounded-full"></div>
            <h3 className="text-lg font-bold text-black">Profit & Loss</h3>
            
            {/* Wallet Selector Dropdown */}
            <GlobalDropdown
              options={availableWallets.map(wallet => ({
                value: wallet.address,
                label: wallet.name,
                subtitle: `${wallet.address.slice(0, 8)}...${wallet.address.slice(-4)}`,
                badge: wallet.isActive ? 'ACTIVE' : undefined,
                badgeColor: 'active',
                balance: `${(wallet.balance || 0).toFixed(3)} SOL`,
                usdValue: wallet.balance && wallet.balance > 0 ? `$${(wallet.balance * 200).toFixed(0)}` : '$0'
              }))}
              value={currentViewingWallet || ''}
              onChange={(address) => {
                console.log(`🔄 P&L: User selected wallet: ${address.slice(0, 8)}...`);
                setIsSynchronizing(true);
                setCurrentViewingWallet(address);
              }}
              placeholder="Select Wallet"
              className="max-w-[140px]"
            />
            
            {/* Token Count Display */}
            {pnlData && (
              <div className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded-lg">
                {sortedTokenEntries.length} tokens
              </div>
            )}
            
            {/* Refresh Button */}
            <button 
              onClick={handleRefresh} 
              className="p-2 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-600 hover:text-black transition-colors"
              disabled={isLoading}
              title="Refresh data"
            >
              <RefreshCcw size={14} className={isLoading || isAutoRefreshing ? "animate-spin" : ""} />
            </button>
            
            {/* Portfolio Share Button - only show when viewing visible tokens */}
            {!showingHidden && pnlData && (
              <button
                onClick={handleShareWallet}
                className="p-2 rounded-lg bg-[#24c35b]/10 hover:bg-[#24c35b]/20 border border-[#24c35b]/20 hover:border-[#24c35b]/40 transition-all group"
                title="Share portfolio performance"
              >
                <Share2 size={14} className="text-[#24c35b] group-hover:text-white" />
              </button>
            )}
          </div>
          
          {onClose && (
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-50"
              title="Close"
            >
              <X size={16} />
            </button>
          )}
        </div>
        
        {/* Main content with synchronizing state */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {isSynchronizing ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Synchronising wallet P&L data</span>
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-[#24c35b] rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-[#24c35b] rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-2 h-2 bg-[#24c35b] rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </div>
              </div>
            </div>
          ) : (
            content.props.children
          )}
        </div>
      </div>
    );
  }

  // Otherwise, return the modal version with container
  return (
    <div className="p-4 bg-[#16161a] border border-gray-800 rounded-lg">
      <div className="flex items-center justify-between mb-4 p-2 border-b border-[#24c35b] bg-[#16161a] rounded-t-lg">
        <div className="flex items-center gap-2">
          <LineChart size={18} className="text-primary" />
          <h2 className="text-lg font-bold text-white">Profit & Loss</h2>
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            &times;
          </button>
        )}
      </div>
      
      {content}
    </div>
  );
};

// Factory function to create the widget
export const createProfitAndLossWidget = (walletAddress?: string) => {
  return {
    type: 'profit-and-loss',
    title: 'Profit & Loss',
    content: <ProfitAndLoss walletAddress={walletAddress} isWidget={true} />,
    size: { width: 570, height: 300 },
    contentStyle: { 
      padding: 0,
      overflow: 'hidden' 
    },
    glassMorphism: true,
    containerStyle: {
      backgroundColor: 'rgba(12, 20, 26, 0.7)',
      backdropFilter: 'blur(6px)',
      borderRadius: '8px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
      overflow: 'hidden'
    }
  };
};

export default ProfitAndLoss; 
